import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isEqual } from 'lodash';
import moment from 'moment';
import { TASK_BULK_UPLOAD_TEMPLATE } from '../../constants/constants';
import {
  setActiveIncidentRequest,
  setActiveIncidentResponse
} from '../../actions/incidents';
import { loadTask, setActiveTaskRequest } from '../../actions/tasks';
import {
  fetchTasks,
  fetchRepeatingTasks,
  reassignTasks,
  sendTaskReminder,
  fetchUserListFilter,
  updateUserListFilter,
  archiveMany,
  importBulkTaskAssignments
} from '../../api/v4';
import { getLoggedInUser } from '../../selectors/users';
import { usePrevious } from '../../utils/hooks';
import useActiveHeirarchy from '../../utils/useActiveHeirarchy';
import { validListDateRange } from '../../utils/formHelpers';
import customToast from '../../utils/customToast';
import {
  getAddedAttachmentsSelector,
  getAttachmentLoadingSelector
} from '../../selectors/attachments';
import { clearUploadedAttachments } from '../../actions/attachments';

import AnalyticsPeriodPicker from '../../components/AnalyticsPeriodPicker';
import { Button } from '../../components/inputs';
import CSVUploader from '../../components/CSVUploader';
import Header from '../../components/Header';
import HeaderAndFooter from '../../components/HeaderAndFooter';
import ImportProblemsModal from '../../components/Modal/importProblemsModal';
import List from '../../components/List';
import Modal from '../../components/Modal';
import ReassignUserModal from '../../components/Modal/reassignUserModal';
import RepeatableList from '../../components/RepeatableList';
import TypeSelection from '../../components/TypeSelection';

import styles from './taskStages.modules.scss';

export default function TaskStages() {
  const pathname = useLocation().pathname;
  const taskType = pathname.split('/').pop();
  const taskTypeName = taskType === 'task' ? 'Task' : 'Corrective Action';
  const dispatch = useDispatch();
  const setActiveTask = payload => dispatch(setActiveTaskRequest(payload));
  const fetchTask = id => dispatch(loadTask(id));
  const loadIncident = id => dispatch(setActiveIncidentRequest(id));
  const user = useSelector(getLoggedInUser);
  const attachments = useSelector(getAddedAttachmentsSelector);
  const attachmentLoading = useSelector(getAttachmentLoadingSelector);
  const { activeGroup, activeProject } = useActiveHeirarchy();

  const [schedule, setSchedule] = useState('Active');
  const [reassignedUser, setReassignedUser] = useState(null);
  const [ids, setIds] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [groups, setGroups] = useState(false);
  const [active, setActive] = useState([]);
  const [repeating, setRepeating] = useState([]);
  const [empPeriod, setEmpPeriod] = useState();
  const [currentPeriod, setCurrentPeriod] = useState({});
  const [openModalType, setModalType] = useState();
  const [messages, setMessages] = useState([]);

  const prevPeriod = usePrevious(currentPeriod);
  const prevType = usePrevious(schedule);

  useEffect(() => {
    fetchUserListFilter(user._id, 'task').then(r => {
      setCurrentPeriod(r);
      setEmpPeriod(r);
    });
    dispatch(setActiveIncidentResponse({}));
  }, [dispatch, user._id, taskType]);

  useEffect(() => {
    const hasStateChange =
      !isEqual(prevPeriod, currentPeriod) || !isEqual(prevType, schedule);
    if (hasStateChange && validListDateRange(currentPeriod)) {
      if (schedule === 'Active' || schedule === 'Archived')
        fetchTasks(taskType, { ...currentPeriod, selectedType: schedule }).then(
          setActive
        );
      if (user.accessLevel > 100 && taskType === 'task')
        fetchRepeatingTasks().then(setRepeating);
    }
  }, [
    user.accessLevel,
    prevPeriod,
    currentPeriod,
    prevType,
    schedule,
    taskType
  ]);

  const reassign = () => {
    reassignTasks({ ids, reassignedUser }).then(() => {
      setModalType();
      setReassignedUser(null);
      fetchTasks(taskType, { ...currentPeriod, selectedType: schedule }).then(
        setActive
      );
    });
  };

  const doesContainIncidentTask = ids => {
    const selectedTasks = active.filter(t => ids.includes(t._id));

    return selectedTasks.some(t => t.ownerType === 'incident');
  };

  const getGroupIds = ids => {
    const selectedTasks = active.filter(t => ids.includes(t._id));

    return selectedTasks.map(t => t.groupId);
  };

  const handleRowClick = ids => {
    setIds(ids);
    // need to check for incident task to limit to sups or higher
    if (doesContainIncidentTask(ids)) {
      setPermissions([400, 500, 900]);
    } else {
      setPermissions([100, 400, 500, 900]);
    }
    setGroups(getGroupIds(ids));
    setModalType('reassign');
  };

  const handleArchive = ids => {
    archiveMany({
      type: 'task',
      ids: ids,
      isArchived: schedule === 'Active'
    })
      .then(() => {
        fetchTasks(taskType, { ...currentPeriod, selectedType: schedule }).then(
          setActive
        );
      })
      .catch(() =>
        // need to do this in case user tries to unarchive tasks that have archived owners
        fetchTasks(taskType, { ...currentPeriod, selectedType: schedule }).then(
          setActive
        )
      );
  };

  const handleImport = () => {
    try {
      if (!attachments[0]) {
        customToast('File Required', 'error');
      } else {
        importBulkTaskAssignments(attachments[0]?.source_url).then(
          data => {
            customToast('Imported successfully!', 'success');

            fetchTasks(taskType, {
              ...currentPeriod,
              selectedType: schedule
            }).then(setActive);
          },
          error => {
            setMessages(error.data);
            setModalType('importProblems');
          }
        );
      }
    } catch (e) {
      if (e.status !== 400) throw e;

      let messages = e.data;
      if (!Array.isArray(messages)) messages = [messages];
      setMessages(messages);
      setModalType('importProblems');
    }

    dispatch(clearUploadedAttachments());
    setModalType();
  };

  const repeatingSettings = [
    { key: 'label', label: 'Title', accessor: r => r?.task?.label },
    { key: 'description' },
    {
      key: 'assignedTo',
      datatype: 'users',
      label: 'Assignee(s)'
    },
    {
      key: 'repeatingOn',
      datatype: 'dow',
      label: 'Repeat On',
      frequency: 'frequency'
    }
  ];

  let activeSettings = [
    {
      key: 'isCompleted',
      label: 'Status',
      accessor: r =>
        r?.assignOn && moment().isBefore(moment(r.assignOn).startOf('day'))
          ? 'Future Assignment Scheduled'
          : r?.isCompleted
            ? 'Completed'
            : 'Open',
      enum: [
        { value: 'Open', label: 'Open' },
        { value: 'Completed', label: 'Completed' },
        {
          value: 'Future Assignment Scheduled',
          label: 'Future Assignment Scheduled'
        }
      ]
    },
    { key: 'label', label: 'Title' },
    { key: 'description' },
    {
      key: 'assignedTo',
      datatype: 'users',
      label: 'Assignee(s)'
    },
    {
      key: 'dueDate',
      datatype: 'futureDate',
      label: 'Due Date'
    },
    {
      key: 'completionDate',
      datatype: 'futureDate',
      label: 'Completion Date'
    }
  ];

  if (taskType === 'correctiveAction')
    activeSettings.push(
      {
        key: 'parentType',
        label: 'Source'
      },
      {
        key: 'parentNumber',
        label: 'Source Id'
      }
    );

  const header = (
    <Header
      title={`${taskTypeName}s`}
      pageActionOptions={
        taskType === 'task'
          ? [
              {
                label: 'Create Task',
                visible:
                  !activeGroup?.isHidden &&
                  !activeProject?.isHidden &&
                  user.accessLevel > 100,
                color: 'blueOutline',
                onClick: () => setActiveTask({ type: 'task' })
              },
              {
                label: 'Upload Task Assignment Template',
                visible:
                  !activeGroup?.isHidden &&
                  !activeProject?.isHidden &&
                  user?.accessLevel === 900,
                color: 'blueOutline',
                onClick: () => setModalType('bulkUpload')
              }
            ]
          : null
      }
    />
  );

  if (!activeProject?._id) {
    repeatingSettings.unshift({
      key: 'groupId',
      label: 'Group/Est.',
      datatype: 'group'
    });
    activeSettings.unshift({
      key: 'groupId',
      label: 'Group/Est.',
      datatype: 'group'
    });
  }

  if (schedule === 'Archived')
    activeSettings = activeSettings.filter(
      setting => setting.label !== 'Status'
    );

  return (
    <HeaderAndFooter Header={header}>
      {taskType === 'task' ||
      (taskType === 'correctiveAction' && user.accessLevel > 400) ? (
        <TypeSelection
          selected={schedule}
          selectedArray={[
            'Active',
            taskType !== 'correctiveAction' ? 'Repeating' : null,
            user.accessLevel > 400 ? 'Archived' : null
          ]}
          onClick={v => {
            if (v !== schedule) setActive([]);
            setSchedule(v);
          }}
        />
      ) : null}
      {schedule !== 'Repeating' ? (
        <AnalyticsPeriodPicker
          period={currentPeriod}
          onChange={value => {
            setActive([]);
            setCurrentPeriod(value);
          }}
          onSave={v =>
            updateUserListFilter({
              empId: user._id,
              type: 'task',
              period: v
            }).then(() => setEmpPeriod(v))
          }
          allowAll
          savedPeriod={empPeriod}
          listPicker
        />
      ) : null}

      {schedule === 'Repeating' ? (
        <RepeatableList
          repeatables={repeating}
          repeatingColumns={repeatingSettings}
          handleRowClick={row =>
            fetchTask({ id: row._id, fromRepeating: true })
          }
          updateData={() => fetchRepeatingTasks().then(setRepeating)}
          saveKey="repeatingTaskList"
          type="Tasks"
        />
      ) : (
        <List
          saveKey={`${
            schedule === 'Archived' ? 'archived' : ''
          }${taskTypeName}List`}
          rowClick={row =>
            row.type === 'ReportComponent'
              ? loadIncident(row.ownerId)
              : fetchTask(row._id)
          }
          actions={
            user.accessLevel >= 500
              ? [
                  ...(schedule === 'Active'
                    ? [
                        {
                          color: 'blue',
                          label: `Reassign ${taskTypeName}s`,
                          onClick: handleRowClick
                        },
                        {
                          color: 'blue',
                          label: 'Send Reminder',
                          onClick: sendTaskReminder
                        }
                      ]
                    : []),
                  {
                    color: 'blue',
                    label: `${
                      schedule === 'Archived' ? 'Un-' : ''
                    }Archive ${taskTypeName}s`,
                    onClick: handleArchive
                  }
                ]
              : null
          }
          dataIsHash
          data={active}
          settings={activeSettings}
          getRowId={r => r._id}
        />
      )}
      <ReassignUserModal
        title={`Reassign ${taskTypeName}s`}
        reassignUser={reassignedUser}
        disableSubmit={!reassignedUser}
        message={
          <div className={styles.reassignText}>
            All of the selected {taskTypeName}s will be reassigned to the user
            below.
            <span style={{ color: 'var(--color-red)', fontWeight: 'bold' }}>
              {' '}
              If there is a report related {taskTypeName} in the selected group
              of {taskTypeName}s to be reassigned, make sure the user below has
              permissions to all of the incidents associated.
            </span>
          </div>
        }
        onRequestClose={() => {
          setModalType();
          setReassignedUser(null);
        }}
        isOpen={openModalType === 'reassign'}
        employeeFieldLabel="New Assignee"
        onChange={setReassignedUser}
        submitActions={reassign}
        permissions={permissions}
        groups={groups}
      />
      <Modal
        title="Upload Task Assignment Template"
        titleClassName="blueHeader"
        isOpen={openModalType === 'bulkUpload'}
        className="modalSmall"
        onRequestClose={() => {
          dispatch(clearUploadedAttachments());
          setModalType();
        }}
        submitButtonColor="blue"
        submitActions={handleImport}
        disableSubmit={attachmentLoading || !attachments?.length}
      >
        <Button
          color="blue"
          onClick={() => {
            var win = window.open(TASK_BULK_UPLOAD_TEMPLATE, '_blank');
            win.focus();
          }}
          text="Download CSV Template"
        />
        <CSVUploader documentType="BulkTaskUpload" className="dropzone" />
      </Modal>

      <ImportProblemsModal
        type="Task"
        isOpen={openModalType === 'importProblems'}
        onRequestClose={() => setModalType()}
        messages={messages}
      />
    </HeaderAndFooter>
  );
}
